import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingUp, PercentCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtPct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const FIELDS = [
  ["purchase_price", 250000],
  ["monthly_rent", 1100],
  ["monthly_costs", 150],
  ["annual_taxes", 800],
  ["loan_amount", 175000],
  ["interest_rate", 3.5],
  ["loan_years", 30],
];

export default function Simulator() {
  const { t } = useI18n();
  const [form, setForm] = useState(Object.fromEntries(FIELDS));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, parseFloat(v) || 0]),
      );
      const res = await api.post("/simulate", payload);
      setResult(res.data);
    } catch (e) {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="simulator-page">
      <div>
        <h1 className="font-display text-4xl">{t("simulator_title")}</h1>
        <p className="text-muted-foreground text-sm mt-2">{t("simulator_sub")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-sm border-border space-y-4">
          {FIELDS.map(([key]) => (
            <div key={key} className="space-y-2">
              <Label>{t(key)}</Label>
              <Input
                type="number"
                data-testid={`sim-${key}`}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="rounded-sm font-mono"
              />
            </div>
          ))}
          <Button onClick={run} disabled={loading} data-testid="sim-calculate" className="w-full rounded-sm gap-2 active:scale-95 transition-transform">
            <Calculator className="h-4 w-4" /> {t("calculate")}
          </Button>
        </Card>

        <div className="space-y-4">
          {!result ? (
            <Card className="p-16 rounded-sm border-dashed border-border text-center h-full flex flex-col items-center justify-center">
              <Calculator className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{t("calculate")}</p>
            </Card>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-6 rounded-sm border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-muted-foreground">{t("gross_yield")}</span>
                    <PercentCircle className="h-4 w-4 text-[hsl(var(--success))]" />
                  </div>
                  <p className="font-display text-3xl tabular-nums">{fmtPct(result.gross_yield)}</p>
                </Card>
                <Card className="p-6 rounded-sm border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase text-muted-foreground">{t("net_yield")}</span>
                    <PercentCircle className="h-4 w-4 text-[#3B82F6]" />
                  </div>
                  <p className="font-display text-3xl tabular-nums">{fmtPct(result.net_yield)}</p>
                </Card>
              </div>

              <Card className="p-6 rounded-sm border-border">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-4 w-4 text-[hsl(var(--success))]" />
                  <span className="text-sm uppercase tracking-wider text-muted-foreground">Cashflow</span>
                </div>
                <div className="space-y-3">
                  {[
                    [t("monthly_cashflow"), result.monthly_cashflow],
                    [t("annual_cashflow"), result.annual_cashflow],
                    [t("loan_payment"), result.monthly_loan_payment],
                    [t("equity"), result.equity_invested],
                  ].map(([label, val]) => (
                    <div key={label} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <span className="text-sm text-muted-foreground">{label}</span>
                      <span className={`font-display text-xl tabular-nums ${val < 0 ? "text-[hsl(var(--destructive))]" : ""}`}>
                        {fmtCurrency(val)}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-sm text-muted-foreground">{t("cash_on_cash")}</span>
                    <span className="font-display text-xl tabular-nums text-[hsl(var(--success))]">{fmtPct(result.cash_on_cash)}</span>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
